/**
 * Recommendation engine (av-methodology-classification spec).
 *
 * Given a resolved matrix position, returns recommended communication
 * elements and sound resources sourced entirely from JSON
 * (`recommendations.json`, `audio-resources.json`) — no recommendation text
 * is authored in this module or in the UI. Historical (2010) content and
 * current (2026 rule-engine) content are kept distinguishable in the output,
 * never merged into a single unattributed recommendation.
 *
 * Design D29: this module already only ever worked from `MatrixPositionId`
 * (structural — archetypeId/relevance) and `classify()`'s position-level
 * data (`elementos`/`sonoro`), never from a reference-language *label* —
 * confirmed unchanged by this decision. It is therefore already consistent
 * with the position/reference-language split: reference-language identity
 * (`reference-language.ts`, `reference-languages.json`) is a concern of the
 * *interpretation* layer (`hybridization-interpretation.ts`, `results-panel.ts`),
 * layered on top of — never inside — this module.
 */

import { audioResources, recommendations } from "./data-source";
import { classify } from "./classification";
import { deriveCommunicationalFacts, describeSoundIndication, POSITION_FACTS_ORIGIN } from "./position-facts";
import type { CommunicationalFacts, MatrixPositionId, RecommendationRule } from "./types";

export interface CodecInfo {
  id: string;
  label: string;
  status: string;
  provenance?: Record<string, unknown>;
}

export interface HistoricalRecommendation {
  origin: "ferreira-2010";
  elementos: string;
  audio?: string[];
  video?: string[];
  distribuicao?: string[];
}

export interface CurrentRecommendation {
  origin: "review-2026";
  ruleId: string;
  technologyClass?: string;
  preferredOptions: CodecInfo[];
  delivery?: string[];
  message?: string;
}

export type RecommendationResult =
  | {
      status: "recorded";
      position: MatrixPositionId;
      facts: CommunicationalFacts;
      factsOrigin: typeof POSITION_FACTS_ORIGIN;
      /** Methodology-layer statement, shown BEFORE the technology-catalog blocks below. */
      methodologicalStatement: string;
      historical: HistoricalRecommendation;
      current: CurrentRecommendation | null;
    }
  | {
      status: "not-recorded";
      position: MatrixPositionId;
      message: string;
    };

function ruleMatches(rule: RecommendationRule, facts: CommunicationalFacts): boolean {
  return (Object.keys(rule.when) as Array<keyof CommunicationalFacts>).every(
    (key) => rule.when[key] === facts[key],
  );
}

function resolveCodec(id: string): CodecInfo {
  const codec = audioResources.codecs.find((c) => c.id === id);
  if (!codec) return { id, label: id, status: "unknown" };
  return { id: codec.id, label: codec.label, status: codec.status, provenance: codec.provenance };
}

/**
 * Given a matrix position, return its recommendation — historical (2010)
 * and current (2026) content kept distinct — or an explicit "not recorded"
 * result if the position has no migrated historical data (spec: positions
 * without recorded recommendations degrade explicitly, never crash or
 * fabricate content).
 */
export function getRecommendation(position: MatrixPositionId): RecommendationResult {
  const classified = classify(position); // throws on unknown archetype/relevance

  if (!classified.historical) {
    return {
      status: "not-recorded",
      position,
      message: "Sem recomendação registrada ainda para esta posição.",
    };
  }

  const facts = deriveCommunicationalFacts(classified.historical);

  const historical: HistoricalRecommendation = {
    origin: "ferreira-2010",
    elementos: classified.historical.elementos,
    audio: classified.historical.sonoro.audio,
    video: classified.historical.sonoro.video,
    distribuicao: classified.historical.sonoro.distribuicao,
  };

  const matchedRule = recommendations.rules.find((rule) => ruleMatches(rule, facts));

  // Only positions with an actual technology suggestion (preferredOptions)
  // get a "current" block — a matched rule that merely confirms "no sound
  // needed" (e.g. `no-sonic-function`, itself origin: ferreira-2010, not a
  // 2026 addition) contributes no technology-catalog block at all, per the
  // spec: "the technology-catalog layer contributes no resource suggestions
  // for this position — the absence is explained, not just implied."
  let current: CurrentRecommendation | null = null;
  if (matchedRule) {
    const rec = matchedRule.recommendation as {
      technologyClass?: string;
      preferredOptions?: string[];
      delivery?: string[];
      message?: string;
    };
    if (rec.preferredOptions && rec.preferredOptions.length > 0) {
      current = {
        origin: "review-2026",
        ruleId: matchedRule.id,
        technologyClass: rec.technologyClass,
        preferredOptions: rec.preferredOptions.map(resolveCodec),
        delivery: rec.delivery,
        message: rec.message,
      };
    }
  }

  return {
    status: "recorded",
    position,
    facts,
    factsOrigin: POSITION_FACTS_ORIGIN,
    methodologicalStatement: describeSoundIndication(facts),
    historical,
    current,
  };
}
